import { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { validationMessages } from 'constants/validation'

import { UserResult, UserNameFormData, useEditSelfUserName } from 'interface/user'
import { Box, Button, Stack, TextField } from '@mui/material'
import { OkIcon } from 'shared/icons'
import { Loading } from 'components/controls/loading'
import { ErrorInfo } from 'components/controls/errorInfo'

const {
  Required,
  TooLongLength,
  TooShortLength
} = validationMessages

const formSchema = yup.object().shape({
  userName: yup.string().required(Required)
    .min(3, TooShortLength(3))
    .max(20, TooLongLength(20))
})
type FormType = yup.InferType<typeof formSchema>

const useUserNameForm = (
	user: UserResult
) => {
  const form = useForm({
    defaultValues: {
			userName: user.userName
    },
    mode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(formSchema)
  })
  const submit = useCallback(
		(input: FormType): UserNameFormData => ({
      userName: input.userName
    }),
    []
  )
  return {
    form,
    submit
  }
}

type UserNameFormProps = {
  user: UserResult
	onChangeUserName: (user: UserResult) => void
}
export const UserNameForm = ({
  user,
  onChangeUserName
}: UserNameFormProps) => {
	const form = useUserNameForm(user)
  const {
    form: {
      handleSubmit,
      control,
      formState: {
        errors,
        isValid
      }
    },
    submit
  } = form

  const [editUserNameState, editUserName] = useEditSelfUserName({
    onEditted: onChangeUserName
  })
  return (
    <Box position="relative">
      {editUserNameState.posting && <Loading/>}
      <form onSubmit={handleSubmit(input => editUserName(submit(input)))}>
        <Stack direction="row">
          <Controller
            control={control}
            name="userName"
            render={({ field }) => (
              <TextField
                {...field}
                variant="outlined"
                size="small"
                error={!!errors.userName}
                helperText={errors.userName ? errors.userName.message : ''}
              />
            )}
          />
          <ErrorInfo error={editUserNameState.error}/>
          <Button
            disabled={!isValid}
            color="primary"
            variant="contained"
            type="submit"
            size="small"
            startIcon={<OkIcon/>}
            sx={{ marginLeft: 1 }}
          >
            更新
          </Button>
        </Stack>
      </form>
    </Box>
  )
}